// Use converter measurements only; dimensions of the folded box are unrelated.
export function detectedThickness(job) {
  const result=job.result||{}, manifest=result.manifest||{}, unfold=result.geometry?.unfold||{};
  if(manifest.state!=='ok'||manifest.method!=='sheetmetal-v2-manual-profile'||manifest.inputSolidCount!==1||manifest.fallbackUsed!==false||(manifest.warnings||[]).length) return null;
  const report=result.thickness_detection, units=report?.units?.toLowerCase()||'mm';
  if(report&&(report.state!=='measured'||report.source!=='SheetMetal.EstimateThickness'))return null;
  const raw=report?report.value:manifest.thicknessMm??unfold.thicknessMm;
  const multiplier={mm:1,inch:25.4,in:25.4}[units];
  const value=raw==null?NaN:Number(raw)*multiplier;
  if(!Number.isFinite(value)||value<=0)return null;
  if([manifest.thicknessMm,unfold.thicknessMm].some(v=>v!=null&&(!Number.isFinite(Number(v))||Math.abs(Number(v)-value)>.001)))return null;
  return value;
}

export function matchThickness(job,sheets,preferredId=null) {
  const value=detectedThickness(job);
  if(value==null)return null;
  const preferred=sheets.find(s=>s.id===preferredId);
  const candidates=preferred?sheets.filter(s=>s.grade_id===preferred.grade_id):sheets;
  if(new Set(candidates.map(s=>s.grade_id)).size!==1)return null;
  // V1 cp: exact within .01 mm; stocked close within max(.12 mm, 4%).
  // Equal nearest distances prefer the thicker gauge. This is a physical-sheet
  // policy, independent of the commercial laser-rate tolerance.
  const gauges=[...new Set(candidates.map(s=>Number(s.thickness_mm)))].filter(v=>v>0).sort((a,b)=>Math.abs(a-value)-Math.abs(b-value)||(b>=value?1:0)-(a>=value?1:0)||a-b);
  const gauge=gauges.find(v=>Math.abs(v-value)<=.01)||gauges[0];
  if(gauge==null||Math.abs(gauge-value)>Math.max(.12,.04*value)+1e-9)return null;
  const matches=candidates.filter(s=>Number(s.thickness_mm)===gauge);
  if(matches.some(s=>s.id===preferredId))return preferredId;
  const sized=preferred?matches.filter(s=>Number(s.width_mm)===Number(preferred.width_mm)&&Number(s.length_mm)===Number(preferred.length_mm)):[];
  return sized.length===1?sized[0].id:matches.length===1?matches[0].id:null;
}

export function refreshMeasuredPreparation(job, draft, sheets, preferredId=null) {
  if(draft?.stock_selection_mode==='auto')return automaticPreparation(job,draft,sheets,preferredId);
  if(draft?.thickness_user_modified||job.quote_preparation?.physical_sheet_id)return draft;
  // Preserve older deliberate sheet corrections, while allowing a grade chosen
  // during conversion to receive its measurement once the worker finishes.
  if(draft?.material_user_modified&&draft.physical_sheet_id&&draft.thickness_user_modified===undefined)return draft;
  if(draft?.material_user_modified&&!draft.material_grade_id)return draft;
  const candidates=draft?.material_grade_id?sheets.filter(sheet=>sheet.grade_id===draft.material_grade_id):sheets;
  return {...draft,physical_sheet_id:matchThickness(job,candidates,draft?.physical_sheet_id||preferredId)};
}

export function chooseMaterialPreparation(job,draft,sheets,grades,familyId,gradeId=null) {
  const gradeIds=new Set(grades.filter(g=>g.family_id===familyId&&(!gradeId||g.id===gradeId)).map(g=>g.id));
  const candidates=sheets.filter(s=>gradeIds.has(s.grade_id));
  const sheetId=matchThickness(job,candidates,draft.physical_sheet_id);
  const next={...draft,physical_sheet_id:sheetId,material_family_id:familyId,
    material_grade_id:gradeId||candidates.find(s=>s.id===sheetId)?.grade_id||(new Set(candidates.map(s=>s.grade_id)).size===1?candidates[0]?.grade_id:null)||null,
    material_user_modified:true,thickness_user_modified:false,review_acknowledged:false,thickness_override_reason:''};
  return draft.stock_selection_mode==='auto'?automaticPreparation(job,next,sheets):next;
}

export function automaticPreparation(job,draft,sheets,preferredId=null) {
  const eligible=sheets.filter(s=>s.enabled!==false&&s.active!==false&&!s.deleted_at&&Number.isInteger(s.price_cents)&&s.price_cents>=0);
  const preferred=eligible.find(s=>s.id===preferredId);
  const grade=draft.material_grade_id||preferred?.grade_id||(new Set(eligible.map(s=>s.grade_id)).size===1?eligible[0]?.grade_id:null);
  const matched=measuredStockChoices(job,eligible.filter(s=>s.grade_id===grade));
  const gauges=[...new Set(matched.map(s=>Number(s.thickness_mm)))];
  return {...draft,stock_selection_mode:'auto',physical_sheet_id:null,material_grade_id:grade||null,
    nominal_thickness_mm:detectedThickness(job)!=null&&gauges.length===1?gauges[0]:null};
}

export function hasStockBasis(prep) {
  return prep?.stock_selection_mode==='auto'?!!(prep.material_grade_id&&prep.nominal_thickness_mm&&prep.grain==='Optional'):!!prep?.physical_sheet_id;
}

export function measuredStockChoices(job,sheets,selectedId=null,showOther=false) {
  const value=detectedThickness(job);
  if(value==null||showOther)return sheets;
  const gauges=[...new Set(sheets.map(sheet=>Number(sheet.thickness_mm)))].filter(gauge=>gauge>0).sort((a,b)=>Math.abs(a-value)-Math.abs(b-value)||(b>=value?1:0)-(a>=value?1:0)||a-b);
  const gauge=gauges.find(g=>Math.abs(g-value)<=.01)||gauges[0];
  const matched=gauge!=null&&Math.abs(gauge-value)<=Math.max(.12,.04*value)+1e-9?gauge:null;
  return sheets.filter(sheet=>Number(sheet.thickness_mm)===matched||sheet.id===selectedId);
}

export function preparationPayload(prep) {
  // Responses include server timestamps/sent metadata; never post these back.
  const keys=['label','quantity','multi_quantities','grain','physical_sheet_id','stock_selection_mode','material_grade_id','nominal_thickness_mm','rate_table_id','operations','operations_user_modified','review_acknowledged','thickness_override_reason'];
  return Object.fromEntries(keys.filter(k=>prep[k]!==undefined).map(k=>[k,prep[k]]));
}
